export type WorkflowCanvasType =
  | "form"
  | "approval"
  | "task"
  | "conditional"
  | "webservice"
  | "email"
  | "pause"
  | "cancel"
  | "finalize";

export type WorkflowFieldDataType =
  | "text"
  | "richtext"
  | "dropdown"
  | "boolean"
  | "file"
  | "number"
  | "date"
  | "userpicker"
  | "datetime";

export type WorkflowFieldVisibility = "show" | "hide";

export interface WorkflowFieldSettings {
  min: number | null;
  max: number | null;
  rows: number | null;
  maxSize: number | null;
  accept: string;
}

/**
 * A loaded row's JSON column, kept so a save rewrites only what the Builder changed. The runtime
 * reads keys the Builder has no editor for (a web-service bodyTemplate, an approver's resolveTo,
 * email cc, a field's isMasked …); rebuilding the column from the UI model alone would drop them.
 */
export interface WorkflowServerJson {
  /** The column as the server returned it. */
  raw: Record<string, unknown>;
  /** What the Builder would have written for the row exactly as loaded — the "untouched" reference. */
  baseline: Record<string, unknown>;
}

export interface WorkflowFormField {
  id: string;
  /** The persisted FieldKey (also the field's variable key, e.g. `pif.lookupFlow`). Unset until the
   *  field is first saved; the graph mapper derives one from the label then, and keeps it stable
   *  across renames. */
  fieldKey?: string;
  label: string;
  dataType: WorkflowFieldDataType;
  required: boolean;
  readOnly: boolean;
  visibility: WorkflowFieldVisibility;
  help: string;
  /** Only rendered while `dataType` is "dropdown", but kept on every other type so switching the
   *  data type back and forth doesn't throw the author's options away. */
  options: string[];
  /** Serialized as the field's SettingsJson. Values stay in the model when the data type changes
   *  so switching back does not silently discard constraints the author entered. */
  settings: WorkflowFieldSettings;
  /** Loaded-only columns the Builder has no editor for, passed back unchanged on save. */
  defaultValue?: string | null;
  validationJson?: string | null;
  /** A loaded option's stored Value by its name (the PIF's "yes" behind "Yes"); new options save name. */
  optionValues?: Record<string, string>;
  serverSettings?: WorkflowServerJson;
}

/**
 * Where a person comes from (Epic 80429 v1) — shared by the canvas owner, the approver source and
 * the task assignee. The reference that goes with it is a Portal user id for "specific-user" (a
 * comma-separated list of them for an approval), a field key for the two field sources, and
 * nothing for the creator sources: the runtime resolves those itself (Epic 80421).
 */
export type WorkflowPersonSource =
  | "specific-user"
  | "creator"
  | "creator-manager"
  | "field-user"
  | "field-user-manager";

export const WORKFLOW_PERSON_SOURCES: { value: WorkflowPersonSource; label: string }[] = [
  { value: "specific-user", label: "Specific user" },
  { value: "creator", label: "Creator" },
  { value: "creator-manager", label: "Creator's manager" },
  { value: "field-user", label: "User from a field" },
  { value: "field-user-manager", label: "Manager of a field user" },
];

export const WORKFLOW_PERSON_SOURCE_LABELS = Object.fromEntries(
  WORKFLOW_PERSON_SOURCES.map(({ value, label }) => [value, label]),
) as Record<WorkflowPersonSource, string>;

/** Field-backed sources name the field the person is read from. */
export const isFieldPersonSource = (source: WorkflowPersonSource | "") =>
  source === "field-user" || source === "field-user-manager";

/** Manager sources walk the Enterprise reports-to chain, so they carry the Enterprise-vs-Portal
 *  disclaimer (a supervisor may not exist yet for a new applicant). */
export const isManagerPersonSource = (source: WorkflowPersonSource | "") =>
  source === "creator-manager" || source === "field-user-manager";

/** The prototype's wording, verbatim. */
export const WORKFLOW_ENTERPRISE_DISCLAIMER =
  "Enterprise vs Portal: “Creator” is a Portal user; a supervisor / manager comes from Enterprise. Some applicants have no Enterprise supervisor until HR sets them up, so this may be empty early on.";

/** How an approval's votes are counted. "any-n" and "percentage" carry a `threshold`. */
export type WorkflowApprovalModel = "single" | "all" | "any-n" | "percentage" | "first";

export const WORKFLOW_APPROVAL_MODELS: { value: WorkflowApprovalModel; label: string }[] = [
  { value: "single", label: "Single approver" },
  { value: "all", label: "All must approve" },
  { value: "any-n", label: "Any N of group" },
  { value: "percentage", label: "Percentage (e.g. 51%)" },
  { value: "first", label: "First response" },
];

export const approvalModelNeedsThreshold = (model: WorkflowApprovalModel) =>
  model === "any-n" || model === "percentage";

export interface WorkflowApprovalConfig {
  model: WorkflowApprovalModel;
  /** N for "any-n", the percent for "percentage"; unused otherwise. */
  threshold: number | null;
  source: WorkflowPersonSource;
  /** Comma-separated Portal user ids for "specific-user"; the field key for a field source. */
  approvers: string;
  acceptRouteId: string;
  rejectRouteId: string;
  cancelRouteId: string;
}

export type WorkflowTaskAssigneeSource = WorkflowPersonSource;

export interface WorkflowTask {
  id: string;
  title: string;
  description: string;
  assigneeSource: WorkflowTaskAssigneeSource;
  assignee: string;
}

export type WorkflowConditionLogic = "AND" | "OR";
export type WorkflowConditionOperator = "is" | "is-not" | "contains" | "greater-than" | "less-than" | "is-empty";

export interface WorkflowConditionCriterion {
  id: string;
  variable: string;
  operator: WorkflowConditionOperator;
  value: string;
}

export interface WorkflowConditionPath {
  id: string;
  name: string;
  logic: WorkflowConditionLogic;
  criteria: WorkflowConditionCriterion[];
  routeId: string;
}

export interface WorkflowKeyValue {
  id: string;
  key: string;
  value: string;
}

export interface WorkflowResponseCapture {
  id: string;
  responseField: string;
  variable: string;
}

export interface WorkflowWebServiceConfig {
  /** PATCH is accepted by the engine; the Builder's verb picker still offers only the other four. */
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  url: string;
  onFailure: "continue" | "cancel";
  params: WorkflowKeyValue[];
  headers: WorkflowKeyValue[];
  captures: WorkflowResponseCapture[];
  /** The request body template (ConfigJson `bodyTemplate`) for POST/PUT. `{{chips}}` go inside
   *  JSON strings; the runtime JSON-escapes each value it substitutes (Epic 80423). */
  body?: string;
}

/** Greater/less-than compare numbers; the runtime would coerce anything else. */
export const WORKFLOW_NUMBER_ONLY_OPERATORS: ReadonlySet<WorkflowConditionOperator> = new Set([
  "greater-than",
  "less-than",
]);

/**
 * Whether a criterion's variable is a number field. Criteria name their variable as free text (a
 * field key, or a field's label before it has a key), resolved against the workflow's form fields.
 */
export const workflowNumberVariableTest = (canvases: WorkflowCanvas[]) => {
  const names = new Set(
    canvases
      .flatMap((canvas) => (canvas.type === "form" ? canvas.fields : []))
      .filter((field) => field.dataType === "number")
      .flatMap((field) => [field.fieldKey, field.label])
      .filter((name): name is string => Boolean(name && name.trim()))
      .map((name) => name.trim().toLowerCase()),
  );
  return (variable: string) => names.has(variable.trim().toLowerCase());
};

/** `{{name}}` / `{{secret:Name}}` chips, as the runtime matches them (WorkflowChipSubstitution). */
export const WORKFLOW_CHIP_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Methods that send a request body. */
export const workflowMethodSendsBody = (method: WorkflowWebServiceConfig["method"]) =>
  method === "POST" || method === "PUT";

/**
 * True when a body template stays valid JSON whatever its chips resolve to. The runtime only
 * escapes a value for a JSON string, so every chip must sit inside quotes: replacing each chip with
 * a bare token proves it - a quoted chip still parses, an unquoted one does not.
 */
export const isValidWorkflowBodyTemplate = (body: string) => {
  if (!body.trim()) return true;
  try {
    JSON.parse(body.replace(WORKFLOW_CHIP_PATTERN, "chip"));
    return true;
  } catch {
    return false;
  }
};

/** Email's own send-to model (Epic 80429 → Email): a named address, or a person resolved at run. */
export type WorkflowEmailRecipientSource = "specific-user" | "dynamic-user" | "dynamic-manager";

export const WORKFLOW_EMAIL_RECIPIENT_SOURCES: {
  value: WorkflowEmailRecipientSource;
  label: string;
}[] = [
  { value: "specific-user", label: "Specific user" },
  { value: "dynamic-user", label: "Dynamic user" },
  { value: "dynamic-manager", label: "Dynamic manager" },
];

export interface WorkflowEmailConfig {
  recipientSource: WorkflowEmailRecipientSource;
  recipient: string;
  subject: string;
  body: string;
  attachment: string;
}

export type WorkflowPauseUnit = "hours" | "days" | "working-days";

export interface WorkflowPauseConfig {
  amount: number;
  /** "" = not set: a loaded unit the runtime doesn't recognise (or none). Validate flags it so the
   *  author picks one, instead of the Builder silently choosing a wait length for them. */
  unit: WorkflowPauseUnit | "";
  routeId: string;
}

export interface WorkflowCancelConfig {
  noteRequired: boolean;
  defaultNote: string;
}

export interface WorkflowCanvas {
  id: string;
  type: WorkflowCanvasType;
  name: string;
  summary: string;
  /**
   * The stage's stable identity across saves, versions and duplicates (Epic 80429). Generated once
   * when the canvas is created and never regenerated — a rename or reorder keeps it — so anything
   * keyed on a stage (the runtime, a PIF hook) survives edits. Unique within the definition.
   * "" = a saved stage whose key the server didn't return; it is never filled in client-side, since
   * an existing stage's key (e.g. the PIF's pif-1..pif-9) must not be rewritten. The Run screen's
   * bespoke PIF stages key on this (Epic 80430), never on `id`, `name` or position.
   */
  stageKey: string;
  /** Who is responsible for this canvas; "" = nobody named (it defaults at runtime). */
  ownerSource: WorkflowPersonSource | "";
  /** The owner reference — a user id or a field key, per ownerSource (see WorkflowPersonSource). */
  owner: string;
  fields: WorkflowFormField[];
  approval: WorkflowApprovalConfig;
  tasks: WorkflowTask[];
  paths: WorkflowConditionPath[];
  webService: WorkflowWebServiceConfig;
  email: WorkflowEmailConfig;
  pause: WorkflowPauseConfig;
  cancel: WorkflowCancelConfig;
  /** A loaded stage's ConfigJson (see WorkflowServerJson); absent for a canvas made in the Builder. */
  serverConfig?: WorkflowServerJson;
  /**
   * false for a loaded Conditional that had no catch-all `default` transition (the PIF's "Who is
   * this?" routes every case by path). The Builder otherwise adds one to the next canvas, which on
   * such a stage would silently change where an unmatched case goes.
   */
  catchAllToNext?: boolean;
}

export const WORKFLOW_FIELD_DATA_TYPES: { value: WorkflowFieldDataType; label: string }[] = [
  // v1 types in the prototype order. No tree-dropdown in v1.
  { value: "text", label: "Text" },
  { value: "richtext", label: "Rich text" },
  { value: "dropdown", label: "Dropdown" },
  { value: "userpicker", label: "User picker" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & time" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "file", label: "File" },
];

export interface WorkflowCanvasTypeConfig {
  type: WorkflowCanvasType;
  label: string;
  description: string;
}

export const WORKFLOW_CANVAS_TYPES: WorkflowCanvasTypeConfig[] = [
  { type: "form", label: "Form", description: "Collect information with configurable fields." },
  { type: "approval", label: "Approval", description: "Route a decision to one or more approvers." },
  { type: "task", label: "Task", description: "Assign a checklist of work to complete." },
  { type: "conditional", label: "Conditional", description: "Branch the flow using ordered conditions." },
  { type: "webservice", label: "Web service", description: "Call an endpoint and capture its response." },
  { type: "email", label: "Email", description: "Send a message to a static or dynamic recipient." },
  { type: "pause", label: "Pause", description: "Wait for an amount of time before continuing." },
  { type: "cancel", label: "Cancel", description: "End the case with an optional reason." },
];

/**
 * The route value for "End workflow" on a gate, path or pause — a transition with no target. The
 * runtime ends the case there (Completed, or Cancelled on the Cancel gate; Epic 80421's engine).
 */
export const WORKFLOW_END_ROUTE = "end-workflow";
export const WORKFLOW_END_ROUTE_LABEL = "End workflow";

/** A route's display name: the target canvas, "End workflow", or undefined when it goes nowhere
 *  (unset, or a canvas deleted since — which Validate treats as unrouted). */
export const describeWorkflowRoute = (routeId: string, canvases: Pick<WorkflowCanvas, "id" | "name">[]) =>
  routeId === WORKFLOW_END_ROUTE
    ? WORKFLOW_END_ROUTE_LABEL
    : routeId
      ? canvases.find((canvas) => canvas.id === routeId)?.name
      : undefined;

export const WORKFLOW_CANVAS_LABELS: Record<WorkflowCanvasType, string> = {
  form: "Form",
  approval: "Approval",
  task: "Task",
  conditional: "Conditional",
  webservice: "Web service",
  email: "Email",
  pause: "Pause",
  cancel: "Cancel",
  finalize: "Finalize",
};

// The Builder editors and the runtime stages both label these, so they read one map — a criterion
// that says "is not" in WorkflowConditionalPanel must not read "≠" on the Test / Run screen.
export const WORKFLOW_CONDITION_OPERATOR_LABELS: Record<WorkflowConditionOperator, string> = {
  is: "is",
  "is-not": "is not",
  contains: "contains",
  "greater-than": ">",
  "less-than": "<",
  "is-empty": "is empty",
};

export const WORKFLOW_TASK_ASSIGNEE_LABELS: Record<WorkflowTaskAssigneeSource, string> =
  WORKFLOW_PERSON_SOURCE_LABELS;

/**
 * A person source as a short readable label — the owner chip on a tile, the assignee on the Run
 * screen. A specific user shows the name when the caller can look it up, otherwise their id.
 */
export const describeWorkflowPerson = (
  source: WorkflowPersonSource | "",
  ref: string,
  userName?: (userId: string) => string | undefined,
): string => {
  if (!source) return "";
  if (source === "specific-user") {
    const ids = ref.split(",").map((id) => id.trim()).filter(Boolean);
    if (ids.length === 0) return WORKFLOW_PERSON_SOURCE_LABELS[source];
    // A non-numeric reference is a pre-v1 display name (the static seeds); shown as written.
    return ids
      .map((id) => (/^\d+$/.test(id) ? userName?.(id) ?? `User #${id}` : id))
      .join(", ");
  }
  if (isFieldPersonSource(source) && ref) {
    return `${WORKFLOW_PERSON_SOURCE_LABELS[source]} (${ref})`;
  }
  return WORKFLOW_PERSON_SOURCE_LABELS[source];
};

// Field ids only have to be unique within the session — they key React lists and identify the row
// being edited. A module-level counter keeps them unique across canvases and across reopening the
// dialog, which a component-scoped ref would not.
let fieldSequence = 0;
let taskSequence = 0;
let configSequence = 0;

export const createWorkflowFormField = (
  overrides: Partial<Omit<WorkflowFormField, "id">> = {},
): WorkflowFormField => ({
  id: `field-${++fieldSequence}`,
  label: "",
  dataType: "text",
  required: false,
  readOnly: false,
  visibility: "show",
  help: "",
  options: [],
  settings: {
    min: null,
    max: null,
    rows: null,
    maxSize: null,
    accept: "",
  },
  ...overrides,
});

export const createWorkflowApprovalConfig = (
  overrides: Partial<WorkflowApprovalConfig> = {},
): WorkflowApprovalConfig => ({
  model: "single",
  threshold: null,
  source: "specific-user",
  approvers: "",
  acceptRouteId: "",
  rejectRouteId: "",
  cancelRouteId: "",
  ...overrides,
});

export const createWorkflowTask = (
  overrides: Partial<Omit<WorkflowTask, "id">> = {},
): WorkflowTask => ({
  id: `task-${++taskSequence}`,
  title: "",
  description: "",
  assigneeSource: "specific-user",
  assignee: "",
  ...overrides,
});

export const createWorkflowCriterion = (): WorkflowConditionCriterion => ({
  id: `criterion-${++configSequence}`,
  variable: "",
  operator: "is",
  value: "",
});

export const createWorkflowConditionPath = (): WorkflowConditionPath => ({
  id: `path-${++configSequence}`,
  name: "Path A",
  logic: "AND",
  criteria: [createWorkflowCriterion()],
  routeId: "",
});

export const createWorkflowKeyValue = (): WorkflowKeyValue => ({
  id: `key-value-${++configSequence}`,
  key: "",
  value: "",
});

export const createWorkflowResponseCapture = (): WorkflowResponseCapture => ({
  id: `capture-${++configSequence}`,
  responseField: "",
  variable: "",
});

const createWorkflowCanvasConfigs = () => ({
  paths: [createWorkflowConditionPath()],
  webService: {
    method: "GET",
    url: "/api/",
    onFailure: "continue",
    params: [],
    headers: [],
    captures: [],
  } as WorkflowWebServiceConfig,
  email: {
    recipientSource: "specific-user",
    recipient: "",
    subject: "",
    body: "",
    attachment: "",
  } as WorkflowEmailConfig,
  pause: {
    amount: 2,
    unit: "working-days",
    routeId: "",
  } as WorkflowPauseConfig,
  cancel: {
    noteRequired: false,
    defaultNote: "",
  } as WorkflowCancelConfig,
});

/** Builds a canvas from the few fields a seed actually cares about, leaving every other config
 *  object at its empty default. Used by the seeded definitions in `workflowDefinitionsData.ts`. */
const STAGE_KEY_MAX = 80;
const STAGE_KEY_SUFFIX = 4;

/**
 * A new stageKey: the stage's name at creation as a kebab slug, plus a short random suffix so two
 * stages created with the same name still differ, e.g. "manager-approval-k3f9". At most 80
 * characters (the StageKey column). Readable in logs and configs; it does NOT follow later renames.
 *
 * `takenKeys` are the keys already in the definition. The suffix makes a clash unlikely, not
 * impossible, and the server refuses a save with two equal keys (Save_IU -2) with an error the
 * author can't fix - so a clash is regenerated here. Compared case-insensitively, as the server's
 * unique index compares them.
 */
export const createStageKey = (name: string, takenKeys: Iterable<string> = []): string => {
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, STAGE_KEY_MAX - STAGE_KEY_SUFFIX - 1)
      .replace(/-+$/, "") || "stage";
  const taken = new Set(Array.from(takenKeys, (key) => key.toLowerCase()));
  const randomSuffix = () =>
    Math.random().toString(36).slice(2, 2 + STAGE_KEY_SUFFIX).padEnd(STAGE_KEY_SUFFIX, "0");
  let key = `${slug}-${randomSuffix()}`;
  // 36^4 suffixes per slug, so this only loops on an actual clash; the counter is a final fallback.
  for (let attempt = 0; taken.has(key.toLowerCase()); attempt += 1) {
    key = attempt < 20 ? `${slug}-${randomSuffix()}` : `${slug}-${randomSuffix()}${attempt}`;
  }
  return key;
};

export const createSeedCanvas = (
  seed: Pick<WorkflowCanvas, "id" | "type" | "name" | "summary"> & Partial<WorkflowCanvas>,
): WorkflowCanvas => ({
  stageKey: createStageKey(seed.name),
  ownerSource: "",
  owner: "",
  fields: [],
  approval: createWorkflowApprovalConfig(),
  tasks: [],
  ...createWorkflowCanvasConfigs(),
  ...seed,
});

/** "New workflow" starts where the prototype's `createWorkflow()` starts — every workflow opens
 *  with a Form and ends with Finalize. */
export const createStarterWorkflowCanvases = (): WorkflowCanvas[] => [
  createSeedCanvas({
    id: "canvas-start-form",
    type: "form",
    name: "New form",
    summary: "Collect information.",
    fields: [createWorkflowFormField({ label: "Field 1" })],
  }),
  createSeedCanvas({
    id: "canvas-start-finalize",
    type: "finalize",
    name: "Finalize",
    summary: "Close out the case.",
  }),
];

export const createWorkflowCanvas = (
  type: WorkflowCanvasType,
  sequence: number,
  /** The stage keys already in the workflow, so the new one can't clash with them. */
  takenKeys: Iterable<string> = [],
): WorkflowCanvas => ({
  id: `canvas-${type}-${sequence}`,
  type,
  name: `New ${WORKFLOW_CANVAS_LABELS[type]}`,
  summary:
    WORKFLOW_CANVAS_TYPES.find((canvasType) => canvasType.type === type)?.description ??
    "Complete this workflow step.",
  stageKey: createStageKey(`New ${WORKFLOW_CANVAS_LABELS[type]}`, takenKeys),
  ownerSource: "",
  owner: "",
  // A new Form starts with one field and a new Task with one task, as a new Conditional starts
  // with one path — an "Add" control alone is only for the empty sub-lists (Epic 80429 → Empty).
  fields: type === "form" ? [createWorkflowFormField({ label: "Field 1" })] : [],
  approval: createWorkflowApprovalConfig(),
  tasks: type === "task" ? [createWorkflowTask({ title: "Task 1" })] : [],
  ...createWorkflowCanvasConfigs(),
});
